import React, { useState, useEffect, useCallback } from 'react';
import { Key } from 'lucide-react';
import apiClient from '../../../../api/apiClient';

interface AccountDetailTabProps {
  employeeData: any;
  employeeId: string | null;
}

export default function AccountDetailTab({ employeeData, employeeId }: AccountDetailTabProps) {
  const [userAccount, setUserAccount] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadUserAccount = useCallback(async () => {
    if (!employeeId) return;

    setLoading(true);
    try {
      // Try to get user account by employee_id
      const result = await apiClient.get(`/hris/employees/${employeeId}/user`).catch(() => {
        // If endpoint doesn't exist, try to get from users table
        // For now, we'll use employee_id as NIP
        return {
          nip: employeeData?.employee_id || '-',
          hasAccount: false,
        };
      });

      if (result) {
        setUserAccount(result);
      } else {
        // Fallback: use employee_id as NIP
        setUserAccount({
          nip: employeeData?.employee_id || '-',
          hasAccount: false,
        });
      }
    } catch {
      // Error loading user account
      // Fallback: use employee_id as NIP
      setUserAccount({
        nip: employeeData?.employee_id || '-',
        hasAccount: false,
      });
    } finally {
      setLoading(false);
    }
  }, [employeeId, employeeData]);

  useEffect(() => {
    loadUserAccount();
  }, [loadUserAccount]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const nip = userAccount?.nip || employeeData?.employee_id || '-';
  const hasAccount = userAccount?.hasAccount !== false;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Informasi Akun Login</h3>
            <p className="text-sm text-blue-700">
              Informasi akun yang digunakan untuk login ke sistem
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* NIP */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">NIP</label>
          <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-gray-900 font-medium">{nip}</p>
          </div>
          <p className="text-xs text-gray-500">Digunakan sebagai username untuk login</p>
        </div>

        {/* Account Status */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Status Akun</label>
          <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                hasAccount ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {hasAccount ? 'Aktif' : 'Belum Dibuat'}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            {hasAccount
              ? 'Pegawai dapat login menggunakan NIP dan password'
              : 'Akun login belum dibuat untuk pegawai ini'}
          </p>
        </div>

        {/* Email */}
        {employeeData?.email && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-900">{employeeData.email}</p>
            </div>
          </div>
        )}

        {/* Role */}
        {userAccount?.role && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {userAccount.role === 'admin'
                  ? 'Administrator'
                  : userAccount.role === 'manager'
                    ? 'Manager'
                    : 'Pegawai'}
              </span>
            </div>
          </div>
        )}
      </div>

      {!hasAccount && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Catatan:</strong> Akun login belum dibuat untuk pegawai ini. Silakan edit
            pegawai dan isi informasi akun di tab "Akun Login" untuk membuat akun.
          </p>
        </div>
      )}
    </div>
  );
}
